import AddressAutocomplete from './../common/AddressAutocomplete';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { SearchableMultiSelect, SearchableSelect, VetClinicCombobox } from '../forms/FormUtils';
import MultiItemSelector from '../forms/MultiItemSelector';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';
import { getAvailableUrns, getAvailableKeepsakes, calculateBaseServicePrice, calculateEuthanasiaPrice, getProductPrice, getCustomProductIcon, getProductIcon, formatKeepsakesForDisplay, parseQuantityFromString, convertToCartFormat, calculateTotalCost, initializePricing, getViewingRoomOptions, getViewingRoomPrice } from '../../utils/pricingHelpers';

// Standard weight dropdown values. WooCommerce website orders store the midpoint
// of a weight RANGE (e.g. "20-29 kg" -> 24.5), which matches none of these, so on
// edit the select would blank out and force staff to re-pick (it appeared "wiped").
// We preserve any non-standard stored weight as an extra selectable option.
const STANDARD_WEIGHT_OPTIONS = new Set([
  '0.1', '0.2', '0.5',
  ...Array.from({ length: 70 }, (_, i) => String(i + 1)),
]);

const AddPetModal = ({ onClose, onSuccess, editPet = null, prospectId = null, startAsProspect = false }) => {
  const [configsLoading, setConfigsLoading] = useState(true);
  const [customerLookup, setCustomerLookup] = useState({ found: false, data: null, loading: false });
  const [lookupTimer, setLookupTimer] = useState(null);
  const [saveAsProspect, setSaveAsProspect] = useState(!!prospectId || !!startAsProspect);
  
  // Helper function to convert backend format to cart format for editing
  const convertBackendToCartFormat = (pet) => {
    const cartData = { ...pet };
    
    // Convert urns_selected array to cart format if needed (support both old and new field names)
    if ((pet.urns_selected || pet.urn_selection) && !pet.selected_urns?.length) {
      const urnData = pet.urns_selected || (pet.urn_selection ? [pet.urn_selection] : []);
      cartData.selected_urns = urnData.map(urn => {
        const match = urn.match(/^(\d+)x\s*(.+)$|^(.+)$/);
        if (match) {
          const quantity = match[1] ? parseInt(match[1]) : 1;
          const item = match[2] || match[3];
          return { item: item.trim(), quantity };
        }
        return { item: urn, quantity: 1 };
      });
    }
    
    // Convert keepsakes_selected array to cart format if needed
    if (pet.keepsakes_selected && !pet.selected_keepsakes?.length) {
      cartData.selected_keepsakes = pet.keepsakes_selected.map(keepsake => {
        const match = keepsake.match(/^(\d+)x\s*(.+)$|^(.+)$/);
        if (match) {
          const quantity = match[1] ? parseInt(match[1]) : 1;
          const item = match[2] || match[3];
          return { item: item.trim(), quantity };
        }
        return { item: keepsake, quantity: 1 };
      });
    }
    
    // Convert jewelry_selected array to cart format if needed (support both old and new field names)
    if ((pet.jewelry_selected || pet.jewelry_selection) && !pet.selected_jewelry?.length) {
      const jewelryData = pet.jewelry_selected || (pet.jewelry_selection ? [pet.jewelry_selection] : []);
      cartData.selected_jewelry = jewelryData.map(jewelry => {
        const match = jewelry.match(/^(\d+)x\s*(.+)$|^(.+)$/);
        if (match) {
          const quantity = match[1] ? parseInt(match[1]) : 1;
          const item = match[2] || match[3];
          return { item: item.trim(), quantity };
        }
        return { item: jewelry, quantity: 1 };
      });
    }
    
    return cartData;
  };

  // Add state for transportation section collapse
  const [isTransportationExpanded, setIsTransportationExpanded] = useState(true);
  
  const [formData, setFormData] = useState(() => {
    if (editPet) {
      // Convert backend format to cart format for editing
      const convertedPet = convertBackendToCartFormat(editPet);
      
      return {
        arrival_date: convertedPet.arrival_date || getTodayInAEST(),
        prepayment: convertedPet.prepayment || false,
        prepayment_date: convertedPet.prepayment_date || '',
        client_name: convertedPet.client_name || '',
        phone_number: convertedPet.phone_number || '',
        email: convertedPet.email || '',
        address: convertedPet.address || '',
        postcode: convertedPet.postcode || '',
        vet_clinic: convertedPet.vet_clinic || '',
        vet_staff_benefit: convertedPet.vet_staff_benefit || '',
        pet_name: convertedPet.pet_name || '',
        pet_type: convertedPet.pet_type || 'Dog',
        sex: convertedPet.sex || '',
        breed: convertedPet.breed || '',
        weight_kg: convertedPet.weight_kg || '10',  // Default to 10kg to prevent form validation issues
        age_years: convertedPet.age_years || '',
        age_months: convertedPet.age_months || '',
        microchip_number: convertedPet.microchip_number || '',
        exotic_subcategory: convertedPet.exotic_subcategory || '',
        referral_source: convertedPet.referral_source || 'Vet',
        keepsakes_selected: convertedPet.keepsakes_selected || [],
        keepsakes_notes: convertedPet.keepsakes_notes || '',
        jewelry_notes: convertedPet.jewelry_notes || '',
        certificate_type: convertedPet.certificate_type || 'Digital',
        viewing_room: convertedPet.viewing_room || null,
        viewing_room_datetime: formatDatetimeForInput(convertedPet.viewing_room_datetime),
        pet_delivery_datetime: formatDatetimeForInput(convertedPet.pet_delivery_datetime),
        pet_dropoff_datetime: formatDatetimeForInput(convertedPet.pet_dropoff_datetime),
        home_collection_datetime: formatDatetimeForInput(convertedPet.home_collection_datetime),
        other_appointment_datetime: formatDatetimeForInput(convertedPet.other_appointment_datetime),
        vet_dropoff_datetime: convertedPet.vet_dropoff_datetime ? formatDatetimeForInput(convertedPet.vet_dropoff_datetime) : null,
        euthanasia_service: convertedPet.euthanasia_service || false,
        euthanasia_datetime: formatDatetimeForInput(convertedPet.euthanasia_datetime),
        euthanasia_notes: convertedPet.euthanasia_notes || '',
        transportation_fees: (() => {
          // Handle backward compatibility: convert string to array format
          const transportationData = convertedPet.transportation_fees;
          if (Array.isArray(transportationData)) {
            // Strip old auto-default if it was the only selection and no actual vet collection booked
            if (transportationData.length === 1 && 
                transportationData[0]?.toLowerCase().includes('vet pick') && 
                !convertedPet.other_appointment_datetime) {
              return [];
            }
            return transportationData.length > 0 ? transportationData : [];
          } else if (typeof transportationData === 'string' && transportationData.trim()) {
            if (transportationData.toLowerCase().includes('vet pick') && !convertedPet.other_appointment_datetime) {
              return [];
            }
            return [transportationData];
          } else {
            return [];
          }
        })(),
        urn_selection: convertedPet.urn_selection || '',
        urn_quantity: convertedPet.urn_quantity || 1,
        selected_urns: convertedPet.selected_urns || [], // Now properly converted from backend
        selected_keepsakes: convertedPet.selected_keepsakes || [], // Now properly converted from backend  
        selected_jewelry: convertedPet.selected_jewelry || [], // Now properly converted from backend
        location_area: convertedPet.location_area || 'Not Yet in Our Care',
        location_detail: convertedPet.location_detail || '',
        total_cost: convertedPet.total_cost !== undefined && convertedPet.total_cost !== null ? convertedPet.total_cost.toString() : '',
        payment_method: convertedPet.payment_method || 'Online',
        weekly_pet_number: convertedPet.weekly_pet_number || '',
        status: editPet.status || 'Pending in our care SMS',
        // Enhanced SMS/Email Workflow
        booking_confirmation_sms: editPet.booking_confirmation_sms || false,
        euthanasia_support_sms: editPet.euthanasia_support_sms || false,
        pet_in_care_sms: editPet.pet_in_care_sms || false,
        aquacremation_sms: editPet.aquacremation_sms || false,
        aquacremation_sms_date: formatDatetimeForInput(editPet.aquacremation_sms_date),
        tree_planting_email: editPet.tree_planting_email || false,
        tree_planting_option: editPet.tree_planting_option || 'OTP',
        pickup_ashes_sms: editPet.pickup_ashes_sms || false,
        ashes_collection_booked: editPet.ashes_collection_booked || false,
        ashes_collection_datetime: formatDatetimeForInput(editPet.ashes_collection_datetime),
        certificate_email: editPet.certificate_email || false,
        review_sms: editPet.review_sms || false,
        is_paid: editPet.is_paid || false
      };
    } else {
      return {
        arrival_date: getTodayInAEST(),
        prepayment: false,
        prepayment_date: '',
        client_name: '',
        phone_number: '',
        email: '',
        address: '',
        postcode: '',
        vet_clinic: '',
        pet_name: '',
        pet_type: 'Dog',
        sex: '',
        breed: '',
        weight_kg: '10',  // Default to 10kg to prevent form validation issues
        age_years: '',
        age_months: '',
        microchip_number: '',
        exotic_subcategory: '',
        arrival_method: '',
        referral_source: 'Vet',
        vet_staff_benefit: '',
        keepsakes_selected: [],
        keepsakes_notes: '',
        jewelry_notes: '',
        certificate_type: 'Digital',
        viewing_room: null,
        viewing_room_datetime: null,
        pet_delivery_datetime: null,
        pet_dropoff_datetime: null,
        home_collection_datetime: null,
        other_appointment_datetime: null,
        vet_dropoff_datetime: null,
        transportation_fees: [],
        urn_selection: '',
        urn_quantity: 1,
        selected_urns: [], // Shopping cart style for multiple urns
        selected_keepsakes: [], // Shopping cart style for multiple keepsakes
        selected_jewelry: [], // Shopping cart style for multiple jewelry
        location_area: 'Not Yet in Our Care', // Default location for new pets
        location_detail: '',
        total_cost: '',
        payment_method: 'Online',
        weekly_pet_number: '',
        status: 'Pending in our care SMS',
        // Enhanced SMS/Email Workflow
        booking_confirmation_sms: false,
        euthanasia_support_sms: false,
        pet_in_care_sms: false,
        aquacremation_sms: false,
        aquacremation_sms_date: '',
        tree_planting_email: false,
        tree_planting_option: 'OTP',
        pickup_ashes_sms: false,
        ashes_collection_booked: false,
        ashes_collection_datetime: '',
        certificate_email: false,
        review_sms: false,
        is_paid: false
      };
    }
  });

  const [loading, setLoading] = useState(false);
  const [showKeepsakeConfirmation, setShowKeepsakeConfirmation] = useState(false);
  const [keepsakeData, setKeepsakeData] = useState(null);
  const [autoCalculate, setAutoCalculate] = useState(true);
  const [pricingReady, setPricingReady] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [configs, setConfigs] = useState({
    vetClinics: [],
    keepsakes: [],
    jewelry: [],
    transportation: [],
    urns: {},
    locations: []
  });

  // ── Auto-calculate total when pricing fields change (if in auto mode) ──────
  React.useEffect(() => {
    if (!autoCalculate) return;
    if (!pricingReady) return;  // Don't calculate until pricing data is loaded
    if (!formData.pet_type || !formData.weight_kg) return;
    const calculated = Math.round(calculateTotalCost(
      formData.pet_type, formData.weight_kg,
      formData.selected_urns, formData.selected_keepsakes,
      formData.selected_jewelry, formData.transportation_fees,
      formData.certificate_type, formData.viewing_room, formData.euthanasia_service
    ) * 100) / 100;
    // Apply vet staff 10% discount on top if set
    const final = formData.vet_staff_benefit === 'discount_10'
      ? Math.round(calculated * 0.9 * 100) / 100
      : calculated;

    // When editing: on first load, check if saved total differs from calculated
    // If it does, the staff previously set a manual price — respect it
    if (!initialLoadDone && editPet) {
      setInitialLoadDone(true);
      const savedTotal = parseFloat(editPet.total_cost);
      if (!isNaN(savedTotal) && Math.abs(savedTotal - final) > 0.5) {
        setAutoCalculate(false);  // Staff had a manual price — keep it
        return;
      }
    }

    setFormData(prev => ({ ...prev, total_cost: final }));
  // eslint-disable-next-line
  }, [
    autoCalculate, pricingReady,
    formData.pet_type, formData.weight_kg,
    formData.selected_urns, formData.selected_keepsakes,
    formData.selected_jewelry, formData.transportation_fees,
    formData.certificate_type, formData.viewing_room,
    formData.euthanasia_service,
    formData.vet_staff_benefit
  ]);

  // Customer lookup function
  const lookupCustomerByPhone = async (phoneNumber) => {
    if (!phoneNumber || phoneNumber.length < 8) {
      setCustomerLookup({ found: false, data: null, loading: false });
      return;
    }

    setCustomerLookup({ found: false, data: null, loading: true });
    
    try {
      const response = await axios.get(`${API}/customers/lookup/${encodeURIComponent(phoneNumber)}`);
      
      if (response.data.found) {
        setCustomerLookup({ 
          found: true, 
          data: response.data, 
          loading: false 
        });
      } else {
        setCustomerLookup({ found: false, data: null, loading: false });
      }
    } catch (error) {
      console.error('Customer lookup failed:', error);
      setCustomerLookup({ found: false, data: null, loading: false });
    }
  };

  // Auto-fill customer data function
  const autoFillCustomerData = (customerData) => {
    setFormData(prevData => ({
      ...prevData,
      client_name: customerData.client_name || '',
      email: customerData.email || '',
      address: customerData.address || '',
      postcode: customerData.postcode || ''
    }));
  };

  useEffect(() => {
    const loadConfigs = async () => {
      setConfigsLoading(true);
      await initializePricing(API.replace('/api', ''));
      setPricingReady(true);
      await fetchConfigs();
      setConfigsLoading(false);
    };
    loadConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      console.log("🔄 Loading configuration data...");
      
      // Load configs individually to prevent single failure from breaking all configs
      const configs = {
        vetClinics: [],
        keepsakes: [],
        jewelry: [],
        transportation: [],
        urns: {},
        locations: []
      };

      // Load each config individually with error handling
      try {
        const vetResponse = await apiClient.get('/config/vet-clinics');
        configs.vetClinics = vetResponse.data.vet_clinics || [];
        console.log("✅ Vet clinics loaded:", configs.vetClinics.length, "items");
      } catch (error) {
        console.error("❌ Failed to load vet clinics:", error);
        // Continue with other configs
      }

      try {
        const keepsakeResponse = await apiClient.get('/config/keepsakes');
        configs.keepsakes = keepsakeResponse.data.keepsakes || [];
        console.log("✅ Keepsakes loaded:", configs.keepsakes.length, "items");
      } catch (error) {
        console.error("❌ Failed to load keepsakes:", error);
      }

      try {
        const jewelryResponse = await apiClient.get('/config/jewelry');
        configs.jewelry = jewelryResponse.data.jewelry || [];
        console.log("✅ Jewelry loaded:", configs.jewelry.length, "items");
      } catch (error) {
        console.error("❌ Failed to load jewelry:", error);
      }

      try {
        const transportationResponse = await apiClient.get('/config/transportation');
        configs.transportation = transportationResponse.data.transportation || [];
        console.log("✅ Transportation loaded:", configs.transportation.length, "items");
      } catch (error) {
        console.error("❌ Failed to load transportation:", error);
      }

      try {
        const urnResponse = await apiClient.get('/config/urns');
        configs.urns = urnResponse.data.urns || {};
        const urnGroupCount = Object.keys(configs.urns).length;
        console.log("✅ Urns loaded:", urnGroupCount, "groups");
        console.log("First urn group:", Object.keys(configs.urns)[0]);
      } catch (error) {
        console.error("❌ Failed to load urns:", error);
      }

      try {
        const locationResponse = await apiClient.get('/config/locations');
        configs.locations = locationResponse.data.locations || [];
        console.log("✅ Locations loaded:", configs.locations.length, "items");
      } catch (error) {
        console.error("❌ Failed to load locations:", error);
      }

      setConfigs(configs);
      console.log("🎉 Configuration loading complete");
      
    } catch (error) {
      console.error('❌ Configuration loading failed:', error);
      // Set empty configs to prevent crashes
      setConfigs({
        vetClinics: [],
        keepsakes: [],
        jewelry: [],
        transportation: [],
        urns: {},
        locations: []
      });
    }
  };

  // Keepsake confirmation handlers
  const handleKeepsakeCancelled = () => {
    setShowKeepsakeConfirmation(false);
    setKeepsakeData(null);
  };

  const handleKeepsakeConfirmed = () => {
    setShowKeepsakeConfirmation(false);
    setKeepsakeData(null);
    // Continue with the form submission or other action
    showToast('✅ Keepsakes confirmed as collected!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    console.log('🚀 Form submission started');
    console.log('📝 Edit mode:', !!editPet);
    console.log('📝 Pet ID:', editPet?.id);
    console.log('📝 Form data before processing:', formData);

    try {
      // Validation: Gender must be selected (relaxed in prospect mode)
      if (!saveAsProspect && !formData.sex) {
        showToast('Please select a gender', 'error');
        setLoading(false);
        return;
      }

      // Validation: If prepayment date is set, transportation services must be selected (pet mode only)
      if (!saveAsProspect && formData.prepayment_date) {
        const hasTransportation = formData.transportation_fees && 
          Array.isArray(formData.transportation_fees) && 
          formData.transportation_fees.length > 0 && 
          formData.transportation_fees.some(option => option && option.trim() !== '');
        
        if (!hasTransportation) {
          showToast('❌ Transportation services are required when prepayment date is set', 'error');
          setLoading(false);
          return;
        }
      }

      // Convert cart-style selections back to backend format
      const convertCartToBackendFormat = () => {
        // Convert selected_urns array to urns_selected array (like keepsakes and jewelry)
        const urns_selected = formData.selected_urns 
          ? formData.selected_urns.map(item => 
              item.quantity > 1 ? `${item.quantity}x ${item.item}` : item.item
            )
          : [];

        // Convert selected_keepsakes array to keepsakes_selected array
        const keepsakes_selected = formData.selected_keepsakes 
          ? formData.selected_keepsakes.map(item => 
              item.quantity > 1 ? `${item.quantity}x ${item.item}` : item.item
            )
          : [];

        // Convert selected_jewelry array to jewelry_selected array (like keepsakes)
        const jewelry_selected = formData.selected_jewelry 
          ? formData.selected_jewelry.map(item => 
              item.quantity > 1 ? `${item.quantity}x ${item.item}` : item.item
            )
          : [];

        return {
          urns_selected,
          keepsakes_selected,
          jewelry_selected,
          jewelry_notes: formData.jewelry_notes || '' // Include jewelry notes
        };
      };

      const backendCompatibleFields = convertCartToBackendFormat();
      
      const { selected_urns, selected_keepsakes, selected_jewelry, arrival_method, ...cleanFormData } = formData;
      
      // Helper function to ensure datetime strings have seconds
      const ensureDatetimeSeconds = (datetimeStr) => {
        if (!datetimeStr) return null;
        // If datetime string doesn't have seconds, add ":00"
        if (datetimeStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
          return datetimeStr + ':00';
        }
        return datetimeStr;
      };

      const submitData = {
        ...cleanFormData,
        weight_kg: parseFloat(formData.weight_kg) || 0,
        age_years: formData.age_years ? parseInt(formData.age_years) : null,
        age_months: formData.age_months ? parseInt(formData.age_months) : null,
        total_cost: (formData.total_cost !== null && formData.total_cost !== undefined && formData.total_cost !== '' && formData.total_cost.toString().trim() !== '') ? parseFloat(formData.total_cost) : null,
        aquacremation_sms_date: ensureDatetimeSeconds(formData.aquacremation_sms_date),
        ashes_collection_datetime: ensureDatetimeSeconds(formData.ashes_collection_datetime),
        viewing_room_datetime: ensureDatetimeSeconds(formData.viewing_room_datetime),
        pet_delivery_datetime: ensureDatetimeSeconds(formData.pet_delivery_datetime),
        pet_dropoff_datetime: ensureDatetimeSeconds(formData.pet_dropoff_datetime),
        home_collection_datetime: ensureDatetimeSeconds(formData.home_collection_datetime),
        other_appointment_datetime: ensureDatetimeSeconds(formData.other_appointment_datetime),
        vet_dropoff_datetime: ensureDatetimeSeconds(formData.vet_dropoff_datetime),
        euthanasia_datetime: ensureDatetimeSeconds(formData.euthanasia_datetime),
        prepayment_date: ensureDatetimeSeconds(formData.prepayment_date),
        exotic_subcategory: (formData.pet_type === 'Exotic Pet' && formData.exotic_subcategory) ? formData.exotic_subcategory : null,
        ...backendCompatibleFields
      };

      console.log('📤 Submitting data:', submitData);
      console.log('💰 PAYMENT DEBUG - formData.is_paid:', formData.is_paid);
      console.log('💰 PAYMENT DEBUG - submitData.is_paid:', submitData.is_paid);
      console.log('🔧 MANUAL PRICE DEBUG - formData.total_cost:', formData.total_cost);
      console.log('🔧 MANUAL PRICE DEBUG - submitData.total_cost:', submitData.total_cost);
      console.log('🎯 API endpoint:',
        saveAsProspect ? (prospectId ? `PUT /prospects/${prospectId}` : 'POST /prospects')
        : (editPet?.id ? `PUT /pets/${editPet.id}` : 'POST /pets'));
      console.log('🔧 About to make API call...');

      // ── PROSPECT PATHS ─────────────────────────────────────────────
      if (saveAsProspect) {
        // Map pet form shape → prospect shape
        const prospectPayload = {
          client_name: submitData.client_name || '',
          phone_number: submitData.phone_number || '',
          email: submitData.email || '',
          address: submitData.address || '',
          postcode: submitData.postcode || '',
          pet_name: submitData.pet_name || '',
          pet_type: submitData.pet_type || 'Dog',
          sex: submitData.sex || null,
          breed: submitData.breed || '',
          weight_kg: submitData.weight_kg || 0,
          age_years: submitData.age_years,
          age_months: submitData.age_months,
          vet_clinic: submitData.vet_clinic || null,
          keepsakes_selected: submitData.keepsakes_selected || [],
          urns_selected: submitData.urns_selected || [],
          jewelry_selected: submitData.jewelry_selected || [],
          certificate_type: submitData.certificate_type || null,
          transportation_fees: submitData.transportation_fees || null,
          viewing_room: submitData.viewing_room || null,
          notes: submitData.appointment_notes || '',
          appointment_notes: submitData.appointment_notes || '',
          jewelry_notes: submitData.jewelry_notes || null,
          keepsakes_notes: submitData.keepsakes_notes || null,
          quoted_total: submitData.total_cost,
          referral_source: submitData.referral_source || 'Walk-in',
          source: 'Manual',
        };
        if (prospectId) {
          await apiClient.put(`/prospects/${prospectId}`, prospectPayload);
          console.log('✅ Prospect updated');
        } else {
          await apiClient.post('/prospects', prospectPayload);
          console.log('✅ Prospect created');
        }
      } else if (editPet && editPet.id) {
        console.log('📝 Making PUT request for edit...');
        const response = await apiClient.put(`/pets/${editPet.id}`, submitData);
        console.log('✅ PUT response received:', response.status);
      } else {
        console.log('📝 Making POST request for new pet...');
        const response = await apiClient.post('/pets', submitData);
        console.log('✅ POST response received:', response.status);
        // Activation: if we opened from a prospect with from_prospect_id, link converted pet
        const linkProspectId = editPet?.from_prospect_id || prospectId;
        if (linkProspectId && response?.data?.id) {
          try {
            await apiClient.post(`/prospects/${linkProspectId}/link-converted-pet`, { pet_id: response.data.id });
            console.log('✅ Prospect converted and linked');
          } catch (linkErr) {
            console.error('⚠️ Prospect link failed (non-blocking):', linkErr);
          }
        }
      }
      
      console.log('✅ Form submission successful');
      console.log('🎯 Calling onSuccess callback...');
      showToast('✅ Pet record saved successfully!');
      onSuccess();
    } catch (error) {
      console.error('❌ Pet submission failed:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        stack: error.stack,
        config: {
          method: error.config?.method,
          url: error.config?.url,
          headers: error.config?.headers
        }
      });
      
      if (error.response?.status === 401) {
        // Handle authentication error
        alert(`❌ Authentication Error:\n\nYou need to be logged in to update pet records. Please refresh the page and try again.`);
      } else if (error.response?.status === 409) {
        // Handle duplicate weekly pet number error
        const errorMessage = error.response?.data?.detail || 'Weekly pet number already in use.';
        alert(`⚠️ Duplicate Pet Number:\n\n${errorMessage}`);
      } else if (error.response?.status === 422) {
        const errorDetail = error.response?.data?.detail;
        const errorMessage = typeof errorDetail === 'string' 
          ? errorDetail 
          : JSON.stringify(errorDetail, null, 2);
        alert(`❌ Validation Error:\n\n${errorMessage}`);
      } else if (error.response?.status) {
        // Handle other HTTP errors
        alert(`❌ Server Error (${error.response.status}):\n\n${error.response.data?.detail || error.response.statusText || 'Unknown server error'}`);
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        // Handle network errors
        alert(`❌ Network Error:\n\nCould not connect to server. Please check your internet connection and try again.`);
      } else {
        alert('❌ Failed to save pet record. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isEdit = !!(editPet && editPet.id);
  const isProspectEdit = !!prospectId;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-6 border-b z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              {isProspectEdit ? `Edit Prospect — ${editPet?.pet_name || ''}`
                : isEdit ? `Edit ${editPet.pet_name}`
                : saveAsProspect ? 'New Prospect (Lead)'
                : 'Add New Pet Record'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Prospect Toggle — hidden when editing a committed pet record */}
          {!isEdit && (
            <div className={`mt-4 rounded-lg border-2 p-3 transition ${saveAsProspect ? 'bg-amber-50 border-amber-400' : 'bg-gray-50 border-gray-200'}`}
              data-testid="prospect-toggle-banner">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveAsProspect}
                  onChange={(e) => setSaveAsProspect(e.target.checked)}
                  className="w-5 h-5 accent-amber-600"
                  data-testid="prospect-toggle-checkbox"
                />
                <div className="flex-1">
                  <p className={`font-semibold text-sm ${saveAsProspect ? 'text-amber-900' : 'text-gray-700'}`}>
                    {saveAsProspect ? '🟡 Saving as Prospect (Lead — not yet committed)' : '⚪ Save as Prospect instead?'}
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {saveAsProspect
                      ? 'Only client name + pet name are required. No weekly number, no SMS workflow, no effect on analytics.'
                      : 'Toggle ON for callers who have not committed to the service yet — all fields become optional.'}
                  </p>
                </div>
                {isProspectEdit && !saveAsProspect && (
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-green-100 text-green-800">
                    WILL CONVERT TO PET
                  </span>
                )}
              </label>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Date pet arrived at our care */}
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Date pet arrived at our care</h3>
            <div className="max-w-xs">
              <input
                type="date"
                required={!saveAsProspect}
                value={formData.arrival_date}
                onChange={(e) => setFormData({ ...formData, arrival_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent sticky-field"
              />
              {formData.arrival_date && (
                <p className="text-sm text-blue-600 font-medium mt-2">
                  Day: {getDayOfWeek(formData.arrival_date)}
                </p>
              )}
            </div>
            
            {/* Prepayment Information */}
            <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  id="prepayment"
                  checked={formData.prepayment}
                  onChange={(e) => {
                    setFormData({ 
                      ...formData, 
                      prepayment: e.target.checked,
                      // Clear prepayment_date if prepayment is unchecked
                      prepayment_date: e.target.checked ? formData.prepayment_date : '',
                      // Update location based on prepayment status
                      location_area: e.target.checked ? 'Not Yet in Our Care' : 'Refrigeration'
                    });
                  }}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-3"
                />
                <label htmlFor="prepayment" className="text-sm font-medium text-gray-700">
                  Prepayment
                </label>
                <span className="text-xs text-gray-500 ml-2">(Workflow starts with first SMS)</span>
              </div>
              
              {formData.prepayment && (
                <div className="ml-6 space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Prepayment Date (Optional)
                    </label>
                    <input
                      type="date"
                      min={getTodayInAEST()} // Prevent past dates (Townsville AEST)
                      value={formData.prepayment_date ? formData.prepayment_date.split('T')[0] : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value;
                        const currentTime = formData.prepayment_date ? formData.prepayment_date.split('T')[1] : '09:00';
                        setFormData({ ...formData, prepayment_date: dateValue ? `${dateValue}T${currentTime}` : '' });
                      }}
                      className="w-full max-w-xs px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  {formData.prepayment_date && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Prepayment Time (Optional)
                      </label>
                      <input
                        type="time"
                        value={formData.prepayment_date ? formData.prepayment_date.split('T')[1] || '09:00' : '09:00'}
                        onChange={(e) => {
                          const timeValue = e.target.value;
                          const currentDate = formData.prepayment_date.split('T')[0];
                          setFormData({ ...formData, prepayment_date: `${currentDate}T${timeValue}` });
                        }}
                        className="w-full max-w-xs px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Client Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.client_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Auto-capitalize first letter of each word
                    const capitalized = value.replace(/\b\w/g, l => l.toUpperCase());
                    setFormData({ ...formData, client_name: capitalized });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <input
                  type="tel"
                  required={!saveAsProspect}
                  value={formData.phone_number}
                  onChange={(e) => {
                    const phoneValue = e.target.value;
                    setFormData({ ...formData, phone_number: phoneValue });
                    
                    // Trigger customer lookup after user stops typing (debounce)
                    if (lookupTimer) clearTimeout(lookupTimer);
                    const timer = setTimeout(() => lookupCustomerByPhone(phoneValue), 800);
                    setLookupTimer(timer);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                />
                
                {/* Customer lookup notification */}
                {customerLookup.loading && (
                  <div className="mt-2 p-2 bg-gray-50 rounded border">
                    <div className="text-sm text-gray-600">🔍 Looking up customer...</div>
                  </div>
                )}
                
                {customerLookup.found && (
                  <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-blue-800">
                        👤 <strong>Returning Customer:</strong> {customerLookup.data.customer_data.client_name}
                      </div>
                      <button
                        type="button"
                        onClick={() => autoFillCustomerData(customerLookup.data.customer_data)}
                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Auto-fill
                      </button>
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      {customerLookup.data.total_pets} previous pet{customerLookup.data.total_pets > 1 ? 's' : ''} 
                      {customerLookup.data.last_pet_name && ` • Last: ${customerLookup.data.last_pet_name}`}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  required={!saveAsProspect}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address & Postcode *</label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(address) => setFormData(prev => ({ ...prev, address }))}
                  onPostcodeChange={(postcode) => setFormData(prev => ({ ...prev, postcode }))}
                  placeholder="e.g., 21 Brush Cherry Street, Prospect SA 5082"
                  required={!saveAsProspect}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Start typing Australian address. Postcode will be included automatically.
                </p>
              </div>
            </div>
          </div>

          {/* Pet Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pet Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pet Name *</label>
                <input
                  type="text"
                  required
                  value={formData.pet_name}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setFormData({ ...formData, pet_name: value });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  placeholder="Enter pet name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pet Type *</label>
                <select
                  required={!saveAsProspect}
                  value={formData.pet_type || 'Dog'}
                  onChange={(e) => setFormData({ ...formData, pet_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                >
                  <option value="Dog">Dog</option>
                  <option value="Cat">Cat</option>
                  <option value="Exotic Pet">Exotic Pet</option>
                </select>
              </div>
              {formData.pet_type === 'Exotic Pet' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Exotic Pet Type *</label>
                  <select
                    required={!saveAsProspect}
                    value={formData.exotic_subcategory}
                    onChange={(e) => setFormData({ ...formData, exotic_subcategory: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  >
                    <option value="">Select exotic pet type...</option>
                    <option value="Birds">Birds</option>
                    <option value="Rodents">Rodents</option>
                    <option value="Reptiles">Reptiles</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                <select
                  required={!saveAsProspect}
                  value={formData.sex}
                  onChange={(e) => setFormData({ ...formData, sex: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                >
                  <option value="">Please select gender...</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Breed *</label>
                <input
                  type="text"
                  required={!saveAsProspect}
                  value={formData.breed}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Capitalize first letter, rest lowercase (e.g., labrador → Labrador)
                    const capitalized = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
                    setFormData({ ...formData, breed: capitalized });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg) *</label>
                <select
                  required={!saveAsProspect}
                  value={formData.weight_kg}
                  onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                >
                  <option value="">Select weight...</option>
                  {/* Preserve a non-standard stored weight (e.g. WooCommerce range
                      midpoint like 24.5) so the field isn't blanked on edit and staff
                      aren't forced to re-select it. */}
                  {formData.weight_kg && !STANDARD_WEIGHT_OPTIONS.has(String(formData.weight_kg)) && (
                    <option value={formData.weight_kg}>{formData.weight_kg} kg</option>
                  )}
                  {/* Exotic pets (very small) */}
                  <option value="0.1">0.1 kg</option>
                  <option value="0.2">0.2 kg</option>
                  <option value="0.5">0.5 kg</option>
                  {/* Small pets - sequential every 1kg */}
                  <option value="1">1 kg</option>
                  <option value="2">2 kg</option>
                  <option value="3">3 kg</option>
                  <option value="4">4 kg</option>
                  <option value="5">5 kg</option>
                  <option value="6">6 kg</option>
                  <option value="7">7 kg</option>
                  <option value="8">8 kg</option>
                  <option value="9">9 kg</option>
                  <option value="10">10 kg</option>
                  <option value="11">11 kg</option>
                  <option value="12">12 kg</option>
                  <option value="13">13 kg</option>
                  <option value="14">14 kg</option>
                  <option value="15">15 kg</option>
                  <option value="16">16 kg</option>
                  <option value="17">17 kg</option>
                  <option value="18">18 kg</option>
                  <option value="19">19 kg</option>
                  <option value="20">20 kg</option>
                  <option value="21">21 kg</option>
                  <option value="22">22 kg</option>
                  <option value="23">23 kg</option>
                  <option value="24">24 kg</option>
                  <option value="25">25 kg</option>
                  <option value="26">26 kg</option>
                  <option value="27">27 kg</option>
                  <option value="28">28 kg</option>
                  <option value="29">29 kg</option>
                  <option value="30">30 kg</option>
                  <option value="31">31 kg</option>
                  <option value="32">32 kg</option>
                  <option value="33">33 kg</option>
                  <option value="34">34 kg</option>
                  <option value="35">35 kg</option>
                  <option value="36">36 kg</option>
                  <option value="37">37 kg</option>
                  <option value="38">38 kg</option>
                  <option value="39">39 kg</option>
                  <option value="40">40 kg</option>
                  <option value="41">41 kg</option>
                  <option value="42">42 kg</option>
                  <option value="43">43 kg</option>
                  <option value="44">44 kg</option>
                  <option value="45">45 kg</option>
                  <option value="46">46 kg</option>
                  <option value="47">47 kg</option>
                  <option value="48">48 kg</option>
                  <option value="49">49 kg</option>
                  <option value="50">50 kg</option>
                  <option value="51">51 kg</option>
                  <option value="52">52 kg</option>
                  <option value="53">53 kg</option>
                  <option value="54">54 kg</option>
                  <option value="55">55 kg</option>
                  <option value="56">56 kg</option>
                  <option value="57">57 kg</option>
                  <option value="58">58 kg</option>
                  <option value="59">59 kg</option>
                  <option value="60">60 kg</option>
                  <option value="61">61 kg</option>
                  <option value="62">62 kg</option>
                  <option value="63">63 kg</option>
                  <option value="64">64 kg</option>
                  <option value="65">65 kg</option>
                  <option value="66">66 kg</option>
                  <option value="67">67 kg</option>
                  <option value="68">68 kg</option>
                  <option value="69">69 kg</option>
                  <option value="70">70 kg</option>
                  <option value="71">71+ kg</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age (years)</label>
                <input
                  type="number"
                  value={formData.age_years}
                  onChange={(e) => setFormData({ ...formData, age_years: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Microchip (#)</label>
                <input
                  type="text"
                  value={formData.microchip_number || ''}
                  onChange={(e) => setFormData({ ...formData, microchip_number: e.target.value })}
                  placeholder="Enter microchip number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                />
              </div>

              {/* Base Service Price Display - MOVED BELOW Age (years) */}
              {formData.pet_type && formData.weight_kg && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Base Service Price:</span>
                    <span className="text-lg font-bold text-blue-600">
                      ${calculateBaseServicePrice(formData.pet_type, formData.weight_kg)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Includes {formData.sex === 'M' ? 'Blue' : formData.sex === 'F' ? 'Pink' : ''} Pouch + Candle
                  </p>
                </div>
              )}

              {/* Pet Euthanasia add-on */}
              {formData.pet_type && formData.weight_kg && (
                <div className="mt-4 p-4 bg-rose-50 rounded-lg border-l-4 border-rose-400" data-testid="euthanasia-section">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">Pet Euthanasia (add-on service)</span>
                    <input
                      type="checkbox"
                      checked={!!formData.euthanasia_service}
                      onChange={(e) => setFormData({ ...formData, euthanasia_service: e.target.checked })}
                      className="h-4 w-4 text-rose-600 rounded"
                      data-testid="euthanasia-toggle"
                    />
                  </label>
                  {formData.euthanasia_service && (
                    <div className="mt-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Euthanasia fee (by weight):</span>
                        <span className="text-base font-bold text-rose-600" data-testid="euthanasia-price">
                          ${calculateEuthanasiaPrice(formData.weight_kg)}
                        </span>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Euthanasia date &amp; time (optional)</label>
                        <input
                          type="datetime-local"
                          value={formData.euthanasia_datetime || ''}
                          onChange={(e) => setFormData({ ...formData, euthanasia_datetime: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900"
                          data-testid="euthanasia-datetime"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Euthanasia notes (optional)</label>
                        <input
                          type="text"
                          value={formData.euthanasia_notes || ''}
                          onChange={(e) => setFormData({ ...formData, euthanasia_notes: e.target.value })}
                          placeholder="e.g. vet name, sedation notes"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900"
                          data-testid="euthanasia-notes"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Service & Workflow */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Service & Workflow</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setIsTransportationExpanded(!isTransportationExpanded)}
                >
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transportation Services {formData.prepayment_date ? '*' : '(Optional without prepayment date)'}
                  </label>
                  <span className="text-lg text-gray-500">
                    {isTransportationExpanded ? '▼' : '▶'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  {formData.prepayment_date 
                    ? "⚠️ Required: Choose your pet collection and ashes return services for prepayment date"
                    : "💡 Optional: Choose your pet collection and ashes return services (can be set later when prepayment date is scheduled)"
                  }
                </p>
                
                {isTransportationExpanded && (
                  <>
                    {configsLoading ? (
                      <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                        Loading transportation options...
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Pet Transport Services */}
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                          <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                            <span className="mr-2">🚐</span>Pet Transport
                          </h4>
                          <div className="space-y-2">
                            {(() => {
                              const transportOptions = configs.transportation
                                .filter(option => 
                                  !option.toLowerCase().includes('ashes deliver') && 
                                  !option.toLowerCase().includes('customer collects')
                                );
                              // Split into: before home pick-up and home pick-up options
                              const beforeHome = transportOptions.filter(o => !o.toLowerCase().includes('home pick'));
                              const homeOptions = transportOptions.filter(o => o.toLowerCase().includes('home pick'));
                              const allOrdered = [...beforeHome, '__VET_DROPOFF__', ...homeOptions];
                              
                              return allOrdered.map((option, index) => {
                                // Render Vet Drop-off checkbox
                                if (option === '__VET_DROPOFF__') {
                                  return (
                                    <label key="vet-dropoff" className="flex items-center justify-between p-2 bg-white rounded border hover:bg-violet-50 cursor-pointer">
                                      <div className="flex items-center">
                                        <input
                                          type="checkbox"
                                          checked={formData.vet_dropoff_datetime !== null && formData.vet_dropoff_datetime !== undefined}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              const today = getTodayInAEST();
                                              setFormData(prev => ({ ...prev, vet_dropoff_datetime: `${today}T09:00` }));
                                            } else {
                                              setFormData(prev => ({ ...prev, vet_dropoff_datetime: null }));
                                            }
                                          }}
                                          className="rounded border-gray-300 text-violet-600 focus:ring-violet-500 mr-3"
                                          data-testid="vet-dropoff-checkbox"
                                        />
                                        <span className="text-sm text-gray-700">Vet Drop-off (vet delivers to us)</span>
                                      </div>
                                      <span className="text-sm font-medium text-blue-700">Included</span>
                                    </label>
                                  );
                                }
                                // Render regular transport checkbox
                                const isSelected = Array.isArray(formData.transportation_fees) 
                                  ? formData.transportation_fees.includes(option)
                                  : formData.transportation_fees === option;
                                const price = option.match(/\$(\d+)/)?.[1] || '0';
                                const serviceName = option.split('$')[0].trim();
                                return (
                                  <label key={index} className="flex items-center justify-between p-2 bg-white rounded border hover:bg-blue-50 cursor-pointer">
                                    <div className="flex items-center">
                                      <input
                                        type="checkbox"
                                        data-testid={`transport-checkbox-${serviceName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                        checked={isSelected}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setFormData(prev => {
                                            let currentTransportation = Array.isArray(prev.transportation_fees) 
                                              ? [...prev.transportation_fees] 
                                              : [prev.transportation_fees].filter(Boolean);
                                            let newTransportation = [...currentTransportation];
                                            if (checked) {
                                              if (!newTransportation.includes(option)) {
                                                newTransportation.push(option);
                                              }
                                            } else {
                                              newTransportation = newTransportation.filter(item => item !== option);
                                            }
                                            if (newTransportation.length === 0) {
                                              newTransportation = [];
                                            }
                                            const stillHasHomePickup = newTransportation.some(f => f && f.toLowerCase().includes('home pick'));
                                            const stillHasSelfDelivery = newTransportation.some(f => f && f.toLowerCase().includes('self'));
                                            const stillHasVetPickup = newTransportation.some(f => f && f.toLowerCase().includes('vet pick'));
                                            const today = getTodayInAEST();
                                            let vetCollectionDefault = prev.other_appointment_datetime;
                                            if (stillHasVetPickup && !prev.other_appointment_datetime) {
                                              // Work in Townsville local time (AEST/UTC+10) — no DST
                                              const nowAEST = new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
                                              const aestHour = nowAEST.getHours();
                                              const aestDay = nowAEST.getDay();
                                              const isWeekday = aestDay >= 1 && aestDay <= 5;
                                              const isSaturday = aestDay === 6;
                                              const canFitWeekday = isWeekday && aestHour >= 8 && aestHour < 15;
                                              const canFitSaturday = isSaturday && aestHour >= 9 && aestHour < 12;
                                              if (canFitWeekday || canFitSaturday) {
                                                const twoHrs = new Date(nowAEST.getTime() + 2 * 60 * 60 * 1000);
                                                vetCollectionDefault = `${twoHrs.getFullYear()}-${String(twoHrs.getMonth()+1).padStart(2,'0')}-${String(twoHrs.getDate()).padStart(2,'0')}T${String(twoHrs.getHours()).padStart(2,'0')}:${String(twoHrs.getMinutes()).padStart(2,'0')}`;
                                              } else {
                                                const tomorrow = new Date(nowAEST.getTime() + 24 * 60 * 60 * 1000);
                                                while (tomorrow.getDay() === 0) tomorrow.setDate(tomorrow.getDate() + 1);
                                                vetCollectionDefault = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth()+1).padStart(2,'0')}-${String(tomorrow.getDate()).padStart(2,'0')}T09:00`;
                                              }
                                            }
                                            return { 
                                              ...prev, 
                                              transportation_fees: newTransportation,
                                              home_collection_datetime: stillHasHomePickup ? (prev.home_collection_datetime || `${today}T09:00`) : null,
                                              pet_dropoff_datetime: stillHasSelfDelivery ? (prev.pet_dropoff_datetime || `${today}T09:00`) : prev.pet_dropoff_datetime,
                                              other_appointment_datetime: stillHasVetPickup ? vetCollectionDefault : null
                                            };
                                          });
                                        }}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                                      />
                                      <span className="text-sm text-gray-700">{serviceName}</span>
                                    </div>
                                    <span className="text-sm font-medium text-blue-700">
                                      {price === '0' ? 'Included' : `$${price}`}
                                    </span>
                                  </label>
                                );
                              });
                            })()}
                          </div>
                        </div>

                        {/* Vet Drop-off Date/Time Picker — appears when checkbox is ticked */}
                        {formData.vet_dropoff_datetime !== null && formData.vet_dropoff_datetime !== undefined && (
                          <div className="bg-violet-50 p-3 rounded-lg border border-violet-200" data-testid="vet-dropoff-datetime-section">
                            <h4 className="text-sm font-semibold text-violet-900 mb-2">🏥 Vet Drop-off appointment</h4>
                            <div className="flex gap-3">
                              <div className="flex-1">
                                <label className="block text-xs text-gray-600 mb-1">Date</label>
                                <input
                                  type="date"
                                  value={formData.vet_dropoff_datetime ? formData.vet_dropoff_datetime.split('T')[0] : ''}
                                  onChange={(e) => {
                                    const dateValue = e.target.value;
                                    const currentTime = formData.vet_dropoff_datetime ? formData.vet_dropoff_datetime.split('T')[1]?.slice(0, 5) : '09:00';
                                    setFormData(prev => ({ ...prev, vet_dropoff_datetime: dateValue ? `${dateValue}T${currentTime}` : '' }));
                                  }}
                                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500"
                                  data-testid="vet-dropoff-date-input"
                                />
                              </div>
                              {formData.vet_dropoff_datetime && (
                                <div className="w-28">
                                  <label className="block text-xs text-gray-600 mb-1">Time</label>
                                  <input
                                    type="time"
                                    value={formData.vet_dropoff_datetime ? formData.vet_dropoff_datetime.split('T')[1]?.slice(0, 5) || '09:00' : '09:00'}
                                    onChange={(e) => {
                                      const timeValue = e.target.value;
                                      const currentDate = formData.vet_dropoff_datetime.split('T')[0];
                                      setFormData(prev => ({ ...prev, vet_dropoff_datetime: `${currentDate}T${timeValue}` }));
                                    }}
                                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500"
                                    data-testid="vet-dropoff-time-input"
                                  />
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-violet-600 mt-1.5">When the vet will deliver the pet to us.</p>
                          </div>
                        )}
                        {(() => {
                          const hasHomePickup = Array.isArray(formData.transportation_fees) && 
                            formData.transportation_fees.some(f => f && f.toLowerCase().includes('home pick'));
                          if (!hasHomePickup) return null;
                          return (
                            <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200" data-testid="home-collection-datetime-section">
                              <h4 className="text-sm font-semibold text-emerald-900 mb-2">🏡 When should we collect?</h4>
                              <div className="flex gap-3">
                                <div className="flex-1">
                                  <label className="block text-xs text-gray-600 mb-1">Date</label>
                                  <input
                                    type="date"
                                    value={formData.home_collection_datetime ? formData.home_collection_datetime.split('T')[0] : ''}
                                    onChange={(e) => {
                                      const dateValue = e.target.value;
                                      const currentTime = formData.home_collection_datetime ? formData.home_collection_datetime.split('T')[1]?.slice(0, 5) : '09:00';
                                      setFormData(prev => ({ ...prev, home_collection_datetime: dateValue ? `${dateValue}T${currentTime}` : '' }));
                                    }}
                                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                    data-testid="home-collection-date-input"
                                  />
                                </div>
                                {formData.home_collection_datetime && (
                                  <div className="w-28">
                                    <label className="block text-xs text-gray-600 mb-1">Time</label>
                                    <input
                                      type="time"
                                      value={formData.home_collection_datetime ? formData.home_collection_datetime.split('T')[1]?.slice(0, 5) || '09:00' : '09:00'}
                                      onChange={(e) => {
                                        const timeValue = e.target.value;
                                        const currentDate = formData.home_collection_datetime.split('T')[0];
                                        setFormData(prev => ({ ...prev, home_collection_datetime: `${currentDate}T${timeValue}` }));
                                      }}
                                      className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                      data-testid="home-collection-time-input"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Client Drop off Date/Time Picker — shows when Self-delivery by appointment is selected */}
                        {(() => {
                          const hasSelfDelivery = Array.isArray(formData.transportation_fees) && 
                            formData.transportation_fees.some(f => f && f.toLowerCase().includes('self'));
                          if (!hasSelfDelivery) return null;
                          return (
                            <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200" data-testid="dropoff-datetime-section">
                              <h4 className="text-sm font-semibold text-indigo-900 mb-2">Drop off appointment</h4>
                              <div className="flex gap-3">
                                <div className="flex-1">
                                  <label className="block text-xs text-gray-600 mb-1">Date</label>
                                  <input
                                    type="date"
                                    value={formData.pet_dropoff_datetime ? formData.pet_dropoff_datetime.split('T')[0] : ''}
                                    onChange={(e) => {
                                      const dateValue = e.target.value;
                                      const currentTime = formData.pet_dropoff_datetime ? formData.pet_dropoff_datetime.split('T')[1]?.slice(0, 5) : '09:00';
                                      setFormData(prev => ({ ...prev, pet_dropoff_datetime: dateValue ? `${dateValue}T${currentTime}` : '' }));
                                    }}
                                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                    data-testid="dropoff-date-input"
                                  />
                                </div>
                                {formData.pet_dropoff_datetime && (
                                  <div className="w-28">
                                    <label className="block text-xs text-gray-600 mb-1">Time</label>
                                    <input
                                      type="time"
                                      value={formData.pet_dropoff_datetime ? formData.pet_dropoff_datetime.split('T')[1]?.slice(0, 5) || '09:00' : '09:00'}
                                      onChange={(e) => {
                                        const timeValue = e.target.value;
                                        const currentDate = formData.pet_dropoff_datetime.split('T')[0];
                                        setFormData(prev => ({ ...prev, pet_dropoff_datetime: `${currentDate}T${timeValue}` }));
                                      }}
                                      className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                      data-testid="dropoff-time-input"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Vet Collection Date/Time Picker — shows when Vet pick-up is selected */}
                        {(() => {
                          const hasVetPickup = Array.isArray(formData.transportation_fees) && 
                            formData.transportation_fees.some(f => f && f.toLowerCase().includes('vet pick'));
                          if (!hasVetPickup || !formData.other_appointment_datetime) return null;
                          return (
                            <div className="bg-pink-50 p-3 rounded-lg border border-pink-200" data-testid="vet-collection-datetime-section">
                              <h4 className="text-sm font-semibold text-pink-900 mb-2">🚐 Vet Collection appointment</h4>
                              <div className="flex gap-3">
                                <div className="flex-1">
                                  <label className="block text-xs text-gray-600 mb-1">Date</label>
                                  <input
                                    type="date"
                                    value={formData.other_appointment_datetime ? formData.other_appointment_datetime.split('T')[0] : ''}
                                    onChange={(e) => {
                                      const dateValue = e.target.value;
                                      const currentTime = formData.other_appointment_datetime ? formData.other_appointment_datetime.split('T')[1]?.slice(0, 5) : '09:00';
                                      setFormData(prev => ({ ...prev, other_appointment_datetime: dateValue ? `${dateValue}T${currentTime}` : '' }));
                                    }}
                                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                                    data-testid="vet-collection-date-input"
                                  />
                                </div>
                                {formData.other_appointment_datetime && (
                                  <div className="w-28">
                                    <label className="block text-xs text-gray-600 mb-1">Time</label>
                                    <input
                                      type="time"
                                      value={formData.other_appointment_datetime ? formData.other_appointment_datetime.split('T')[1]?.slice(0, 5) || '09:00' : '09:00'}
                                      onChange={(e) => {
                                        const timeValue = e.target.value;
                                        const currentDate = formData.other_appointment_datetime.split('T')[0];
                                        setFormData(prev => ({ ...prev, other_appointment_datetime: `${currentDate}T${timeValue}` }));
                                      }}
                                      className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                                      data-testid="vet-collection-time-input"
                                    />
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-pink-600 mt-1.5">Auto-filled +2hrs. Adjust if needed.</p>
                            </div>
                          );
                        })()}

                        {/* Ashes Return Services */}
                        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                          <h4 className="text-sm font-semibold text-green-900 mb-2 flex items-center">
                            <span className="mr-2">🏠</span>Ashes Return
                          </h4>
                          <div className="space-y-2">
                            {configs.transportation
                              .filter(option => 
                                option.toLowerCase().includes('ashes deliver') || 
                                option.toLowerCase().includes('customer collects')
                              )
                              .map((option, index) => {
                                const isSelected = Array.isArray(formData.transportation_fees) 
                                  ? formData.transportation_fees.includes(option)
                                  : formData.transportation_fees === option;
                                const price = option.match(/\$(\d+)/)?.[1] || '0';
                                const serviceName = option.split('$')[0].trim();
                                return (
                                  <label key={index} className="flex items-center justify-between p-2 bg-white rounded border hover:bg-green-50 cursor-pointer">
                                    <div className="flex items-center">
                                      <input
                                        type="checkbox"
                                        data-testid={`transport-checkbox-${serviceName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                        checked={isSelected}
                                        onChange={(e) => {
                                          // Ensure we're working with an array
                                          let currentTransportation = Array.isArray(formData.transportation_fees) 
                                            ? [...formData.transportation_fees] 
                                            : [formData.transportation_fees].filter(Boolean);
                                            
                                          let newTransportation = [...currentTransportation];
                                          if (e.target.checked) {
                                            if (!newTransportation.includes(option)) {
                                              newTransportation.push(option);
                                            }
                                          } else {
                                            newTransportation = newTransportation.filter(item => item !== option);
                                          }
                                          if (newTransportation.length === 0) {
                                            newTransportation = [];
                                          }
                                          setFormData({ ...formData, transportation_fees: newTransportation });
                                        }}
                                        className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-3"
                                      />
                                      <span className="text-sm text-gray-700">{serviceName}</span>
                                    </div>
                                    <span className="text-sm font-medium text-green-700">
                                      {price === '0' ? 'Included' : `$${price}`}
                                    </span>
                                  </label>
                                );
                              })}
                          </div>
                        </div>

                        {/* Transportation Total */}
                        {(Array.isArray(formData.transportation_fees) ? formData.transportation_fees.length > 0 : Boolean(formData.transportation_fees)) && (
                          <div className="bg-gray-50 p-3 rounded-lg border">
                            <h5 className="text-sm font-medium text-gray-700 mb-1">Selected Services:</h5>
                            {(Array.isArray(formData.transportation_fees) ? formData.transportation_fees : [formData.transportation_fees]).filter(Boolean).map((option, index) => {
                              const price = option.match(/\$(\d+)/)?.[1] || '0';
                              const serviceName = option.split('$')[0].trim();
                              return (
                                <div key={index} className="flex justify-between text-sm text-gray-600">
                                  <span>• {serviceName}</span>
                                  <span>{price === '0' ? 'Included' : `$${price}`}</span>
                                </div>
                              );
                            })}
                            <div className="border-t mt-2 pt-2 flex justify-between text-sm font-medium text-gray-800">
                              <span>Transportation Total:</span>
                              <span>
                                ${(Array.isArray(formData.transportation_fees) ? formData.transportation_fees : [formData.transportation_fees]).filter(Boolean).reduce((total, option) => {
                                  const price = parseInt(option.match(/\$(\d+)/)?.[1] || '0');
                                  return total + price;
                                }, 0)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            
            {/* Vet Clinic - moved from Client Information */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Vet Clinic</label>
              <VetClinicCombobox
                value={formData.vet_clinic}
                onChange={(value) => setFormData({ ...formData, vet_clinic: value })}
                options={configs.vetClinics}
              />
            </div>
            
            {/* Multi-Item Urn Selection */}
            <MultiItemSelector
              title="Urns"
              availableItems={getAvailableUrns(formData.pet_type, formData.weight_kg, formData.sex)}
              selectedItems={formData.selected_urns}
              onItemsChange={(items) => setFormData({ ...formData, selected_urns: items })}
              placeholder="Select urn..."
              type="grouped"
            />
            
            {/* Multi-Item Keepsake Selection */}
            <MultiItemSelector
              title="Keepsakes"
              availableItems={configs.keepsakes || []}
              selectedItems={formData.selected_keepsakes}
              onItemsChange={(items) => setFormData({ ...formData, selected_keepsakes: items })}
              placeholder="Select keepsake..."
              type="single"
            />
            
            {/* Multi-Item Jewelry Selection */}
            <MultiItemSelector
              title="Jewelry"
              availableItems={configs.jewelry}
              selectedItems={formData.selected_jewelry}
              onItemsChange={(items) => setFormData({ ...formData, selected_jewelry: items })}
              placeholder="Select jewelry..."
              type="single"
            />

            {/* Jewelry Notes */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.jewelry_notes || ''}
                onChange={(e) => setFormData({ ...formData, jewelry_notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                rows="3"
                placeholder=""
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Tip: Each line will automatically display as a bullet point
              </p>
            </div>

            {/* Certificate Type */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Type</label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="certificate_type"
                    value="Digital"
                    checked={formData.certificate_type === 'Digital'}
                    onChange={(e) => setFormData({ ...formData, certificate_type: e.target.value })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                  />
                  <span className="text-sm text-gray-700">Digital (Default)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="certificate_type"
                    value="Printed"
                    checked={formData.certificate_type === 'Printed'}
                    onChange={(e) => setFormData({ ...formData, certificate_type: e.target.value })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                  />
                  <span className="text-sm text-gray-700">Printed</span>
                </label>
              </div>
            </div>

            {/* Viewing Room */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Viewing Room (Optional)</label>
              <select
                value={formData.viewing_room || ''}
                data-testid="viewing-room-select"
                onChange={(e) => setFormData({ ...formData, viewing_room: e.target.value || null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              >
                <option value="">No viewing room</option>
                {getViewingRoomOptions().map(vr => (
                  <option key={vr.name} value={vr.name}>{vr.name} - ${vr.price}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Optional viewing room hire for families
              </p>
              
              {/* Viewing Room Datetime Picker - Show when viewing room is selected */}
              {formData.viewing_room && (
                <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-3">
                  <label className="block text-sm font-medium text-purple-800">
                    👁️ Schedule Viewing Room Appointment
                  </label>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      min={getTodayInAEST()}
                      value={formData.viewing_room_datetime ? formData.viewing_room_datetime.split('T')[0] : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value;
                        const currentTime = formData.viewing_room_datetime ? formData.viewing_room_datetime.split('T')[1]?.slice(0, 5) : '09:00';
                        setFormData({ ...formData, viewing_room_datetime: dateValue ? `${dateValue}T${currentTime}` : '' });
                      }}
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                    />
                  </div>
                  
                  {formData.viewing_room_datetime && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Time
                      </label>
                      <input
                        type="time"
                        value={formData.viewing_room_datetime ? formData.viewing_room_datetime.split('T')[1]?.slice(0, 5) || '09:00' : '09:00'}
                        onChange={(e) => {
                          const timeValue = e.target.value;
                          const currentDate = formData.viewing_room_datetime.split('T')[0];
                          setFormData({ ...formData, viewing_room_datetime: `${currentDate}T${timeValue}` });
                        }}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                      />
                    </div>
                  )}
                  
                  <p className="text-xs text-purple-600">
                    Set appointment time for viewing room ({formData.viewing_room})
                  </p>
                </div>
              )}
            </div>

            {isEdit && (
              <>
                {/* Enhanced SMS/Email Workflow Section */}
                <div className="mt-6 bg-green-50 p-6 rounded-lg border-l-4 border-green-400">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">📧 SMS/Email Workflow</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center mb-3">
                        <input
                          type="checkbox"
                          checked={formData.booking_confirmation_sms}
                          onChange={(e) => setFormData({ ...formData, booking_confirmation_sms: e.target.checked })}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-3"
                        />
                        <span className="text-sm text-gray-700">Booking Confirmation SMS</span>
                        
                      </label>
                      
                      {/* Repeat customer email template reminder */}
                      {customerLookup.found && (formData.booking_confirmation_sms || formData.pet_in_care_sms || formData.aquacremation_sms || formData.ashes_collection_sms) && (
                        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-center">
                            <span className="text-amber-600 mr-2">⚠️</span>
                            <div className="text-sm text-amber-800">
                              <strong>REPEAT CUSTOMER REMINDER:</strong><br />
                              Use different planting tribute email template for returning clients
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="flex items-center mb-3">
                        <input
                          type="checkbox"
                          checked={formData.euthanasia_support_sms}
                          onChange={(e) => setFormData({ ...formData, euthanasia_support_sms: e.target.checked })}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-3"
                        />
                        <span className="text-sm text-gray-700">Euthanasia Support SMS</span>
                        
                      </label>
                    </div>

                    <div className="bg-red-50 p-3 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.pet_in_care_sms}
                          onChange={(e) => setFormData({ ...formData, pet_in_care_sms: e.target.checked })}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-3"
                        />
                        <span className="text-sm font-medium text-gray-700">Pet in our Care SMS</span>
                        
                      </label>
                    </div>

                    <div className="bg-red-50 p-3 rounded-lg">
                      <label className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          checked={formData.aquacremation_sms}
                          onChange={(e) => setFormData({ ...formData, aquacremation_sms: e.target.checked })}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-3"
                        />
                        <span className="text-sm font-medium text-gray-700">Cremation SMS</span>
                        
                      </label>
                      {formData.aquacremation_sms && (
                        <div className="ml-6 mt-2 space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Cremation Date</label>
                            <input
                              type="date"
                              value={formData.aquacremation_sms_date ? formData.aquacremation_sms_date.split('T')[0] : ''}
                              onChange={(e) => {
                                const dateValue = e.target.value;
                                const currentTime = formData.aquacremation_sms_date ? formData.aquacremation_sms_date.split('T')[1] : '09:00';
                                setFormData({ ...formData, aquacremation_sms_date: dateValue ? `${dateValue}T${currentTime}` : '' });
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          {formData.aquacremation_sms_date && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Time</label>
                              <input
                                type="time"
                                value={formData.aquacremation_sms_date ? formData.aquacremation_sms_date.split('T')[1]?.slice(0, 5) || '09:00' : '09:00'}
                                onChange={(e) => {
                                  const timeValue = e.target.value;
                                  const currentDate = formData.aquacremation_sms_date.split('T')[0];
                                  setFormData({ ...formData, aquacremation_sms_date: `${currentDate}T${timeValue}` });
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="bg-red-50 p-3 rounded-lg">
                      <label className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          checked={formData.tree_planting_email}
                          onChange={(e) => setFormData({ ...formData, tree_planting_email: e.target.checked })}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-3"
                        />
                        <span className="text-sm font-medium text-gray-700">Tree Planting Email</span>
                        
                      </label>
                      {formData.tree_planting_email && (
                        <div className="ml-6 mt-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Tree Planting Option</label>
                          <select
                            value={formData.tree_planting_option || 'OTP'}
                            onChange={(e) => setFormData({ ...formData, tree_planting_option: e.target.value })}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="OTP">OTP - One Tree Planted</option>
                            <option value="CN">CN - Carbon Neutral</option>
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="bg-red-50 p-3 rounded-lg">
                      <label className="flex items-center mb-3">
                        <input
                          type="checkbox"
                          checked={formData.pickup_ashes_sms}
                          onChange={(e) => setFormData({ ...formData, pickup_ashes_sms: e.target.checked })}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-3"
                        />
                        <span className="text-sm font-medium text-gray-700">Pick up Ashes SMS</span>
                        
                      </label>
                    </div>

                    <div className="bg-blue-50 p-3 rounded-lg">
                      <label className="flex items-center mb-3">
                        <input
                          type="checkbox"
                          checked={formData.ashes_collection_booked}
                          onChange={(e) => setFormData({ ...formData, ashes_collection_booked: e.target.checked })}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-3"
                        />
                        <span className="text-sm font-medium text-gray-700">Ashes collection Booked</span>
                        
                      </label>
                      {formData.ashes_collection_booked && (
                        <div className="ml-6 space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Collection Date</label>
                            <input
                              type="date"
                              value={formData.ashes_collection_datetime ? formData.ashes_collection_datetime.split('T')[0] : ''}
                              onChange={(e) => {
                                const dateValue = e.target.value;
                                const currentTime = formData.ashes_collection_datetime ? formData.ashes_collection_datetime.split('T')[1] : '09:00';
                                setFormData({ ...formData, ashes_collection_datetime: dateValue ? `${dateValue}T${currentTime}` : '' });
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          {formData.ashes_collection_datetime && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Collection Time</label>
                              <div className="relative">
                                <input
                                  type="time"
                                  value={formData.ashes_collection_datetime ? formData.ashes_collection_datetime.split('T')[1]?.slice(0, 5) || '09:00' : '09:00'}
                                  onChange={(e) => {
                                    const timeValue = e.target.value;
                                    const currentDate = formData.ashes_collection_datetime.split('T')[0];
                                    setFormData({ ...formData, ashes_collection_datetime: `${currentDate}T${timeValue}` });
                                  }}
                                  className="w-full px-2 py-1 pr-8 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                {/* Custom PS Icon */}
                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                  <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs font-bold" style={{ fontSize: '8px' }}>PS</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="bg-red-50 p-3 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.certificate_email}
                          onChange={(e) => setFormData({ ...formData, certificate_email: e.target.checked })}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-3"
                        />
                        <span className="text-sm font-medium text-gray-700">Certificate Email</span>
                        
                      </label>
                    </div>

                    <div className="bg-red-50 p-3 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.review_sms}
                          onChange={(e) => setFormData({ ...formData, review_sms: e.target.checked })}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-3"
                        />
                        <span className="text-sm font-medium text-gray-700">Review SMS</span>
                        
                      </label>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Auto Status Updates:</strong> When you complete required steps, the workflow status will automatically advance to the next stage.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* On-site Location (NEW) */}
          <div className="bg-indigo-50 p-6 rounded-lg border-l-4 border-indigo-400">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">🏷️ On-site Location</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location Area *</label>
                <select
                  required
                  value={formData.location_area}
                  onChange={(e) => setFormData({ ...formData, location_area: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                >
                  {configs.locations.map((location) => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location Detail</label>
                <input
                  type="text"
                  placeholder="e.g., Shelf B2, Unit 400"
                  value={formData.location_detail}
                  onChange={(e) => setFormData({ ...formData, location_detail: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                />
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              📍 Location tracking is informational only and does not affect workflow status or payment.
            </p>
          </div>

          {/* Payment Information */}
          <div id="payment-information">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" id="payment-section">Payment Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Total Cost</label>
                  {autoCalculate ? (
                    <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                      Auto-calculating
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setAutoCalculate(true)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      🔄 Reset to calculated
                    </button>
                  )}
                </div>
                <input
                  type="number"
                  step="0.01"
                  data-testid="total-cost-input"
                  value={formData.total_cost}
                  onChange={(e) => {
                    setAutoCalculate(false); // Switch to manual mode when staff types
                    setFormData({ ...formData, total_cost: e.target.value });
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 ${autoCalculate ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                />
                {/* Auto-calculated pricing breakdown */}
                {(formData.pet_type && formData.weight_kg) && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-xs font-medium text-blue-800 mb-2">💰 Calculated Pricing:</div>
                    <div className="space-y-1 text-xs text-blue-700">
                      <div className="flex justify-between items-center">
                        <span className="truncate pr-2">Base Service ({formData.pet_type}, {formData.weight_kg}kg):</span>
                        <span className="font-medium text-nowrap">${calculateBaseServicePrice(formData.pet_type, formData.weight_kg)}</span>
                      </div>
                      
                      {formData.selected_urns && formData.selected_urns.length > 0 && formData.selected_urns.map((urn, index) => {
                        const price = getProductPrice(urn.item, 'urn');
                        const quantity = parseInt(urn.quantity) || 1;
                        const totalPrice = price * quantity;
                        
                        return (
                          <div key={index} className="flex justify-between items-center text-purple-700">
                            <span className="truncate pr-2">• {quantity > 1 ? `${quantity}x ` : ''}{urn.item}:</span>
                            <span className="font-medium text-nowrap">${totalPrice}</span>
                          </div>
                        );
                      })}
                      
                      {formData.selected_keepsakes && formData.selected_keepsakes.length > 0 && formData.selected_keepsakes.map((keepsake, index) => {
                        const price = getProductPrice(keepsake.item, 'keepsake', formData.pet_type);
                        const quantity = parseInt(keepsake.quantity) || 1;
                        const totalPrice = price * quantity;
                        
                        return (
                          <div key={index} className="flex justify-between items-center text-blue-700">
                            <span className="truncate pr-2">• {quantity > 1 ? `${quantity}x ` : ''}{keepsake.item}:</span>
                            <span className="font-medium text-nowrap">${totalPrice}</span>
                          </div>
                        );
                      })}
                      
                      {formData.selected_jewelry && formData.selected_jewelry.length > 0 && formData.selected_jewelry.map((jewelry, index) => {
                        const price = getProductPrice(jewelry.item, 'jewelry');
                        const quantity = parseInt(jewelry.quantity) || 1;
                        const totalPrice = price * quantity;
                        
                        return (
                          <div key={index} className="flex justify-between items-center text-amber-700">
                            <span className="truncate pr-2">• {quantity > 1 ? `${quantity}x ` : ''}{jewelry.item}:</span>
                            <span className="font-medium text-nowrap">${totalPrice}</span>
                          </div>
                        );
                      })}
                      
                      {formData.transportation_fees && Array.isArray(formData.transportation_fees) && formData.transportation_fees.map((option, index) => {
                        const price = getProductPrice(option, 'transportation');
                        return price > 0 ? (
                          <div key={index} className="flex justify-between items-center text-green-700">
                            <span className="truncate pr-2">• {option.split('$')[0].trim()}:</span>
                            <span className="font-medium text-nowrap">${price}</span>
                          </div>
                        ) : null;
                      })}
                      
                      {/* Legacy single transportation fee support */}
                      {formData.transportation_fees && !Array.isArray(formData.transportation_fees) && getProductPrice(formData.transportation_fees, 'transportation') > 0 && (
                        <div className="flex justify-between items-center text-green-700">
                          <span className="truncate pr-2">• Transportation:</span>
                          <span className="font-medium text-nowrap">${getProductPrice(formData.transportation_fees, 'transportation')}</span>
                        </div>
                      )}
                      
                      {formData.certificate_type === 'Printed' && (
                        <div className="flex justify-between items-center text-orange-700">
                          <span className="truncate pr-2">• Printed Certificate:</span>
                          <span className="font-medium text-nowrap">${getProductPrice('Printed', 'certificate')}</span>
                        </div>
                      )}
                      
                      {formData.viewing_room && (
                        <div className="flex justify-between items-center text-purple-700">
                          <span className="truncate pr-2">• Viewing Room ({formData.viewing_room}):</span>
                          <span className="font-medium text-nowrap">${getViewingRoomPrice(formData.viewing_room)}</span>
                        </div>
                      )}
                      
                      {formData.euthanasia_service && (
                        <div className="flex justify-between items-center text-rose-700">
                          <span className="truncate pr-2">• Pet Euthanasia:</span>
                          <span className="font-medium text-nowrap">${calculateEuthanasiaPrice(formData.weight_kg)}</span>
                        </div>
                      )}
                      
                      <div className="border-t border-blue-300 pt-1 mt-2">
                        <div className="flex justify-between items-center font-bold text-blue-900">
                          <span className="truncate pr-2">Calculated Total:</span>
                          <span className="text-nowrap">${Math.round(calculateTotalCost(formData.pet_type, formData.weight_kg, formData.selected_urns, formData.selected_keepsakes, formData.selected_jewelry, formData.transportation_fees, formData.certificate_type, formData.viewing_room, formData.euthanasia_service) * 100) / 100}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                >
                  <option value="Online">Online</option>
                  <option value="VetPay">VetPay</option>
                  <option value="EFTPOS">EFTPOS</option>
                  <option value="Cash">Cash</option>
                  <option value="Invoice">Invoice</option>
                  <option value="Afterpay">Afterpay</option>
                  <option value="Clinic Account">Clinic Account</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weekly Pet Number</label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                  <span className="text-gray-600 text-sm">🤖 Auto-assigned based on arrival week</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Automatically calculated and assigned when pet is saved
                </p>
              </div>
            </div>
            
            {/* Payment Status */}
            <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_paid}
                  onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500 mr-3"
                />
                <span className="text-sm font-medium text-gray-700">Payment received</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">Payment status is separate from workflow status</p>
            </div>

            {/* Referral Source - moved from Service & Workflow */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Referral Source *</label>
              <select
                required
                value={formData.referral_source}
                onChange={(e) => setFormData({ ...formData, referral_source: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              >
                <option value="Vet">Vet</option>
                <option value="Google">Google</option>
                <option value="Facebook">Facebook</option>
                <option value="Unknown">Unknown</option>
                <option value="Van">Van</option>
                <option value="Billboard">Billboard</option>
                <option value="Family/Friend">Family/Friend</option>
                <option value="Returning Client">Returning Client</option>
                <option value="Magnet + Flyer">Magnet + Flyer</option>
              </select>
            </div>

            {/* Vet Staff Benefit */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Vet Staff Benefit</label>
              <select
                value={formData.vet_staff_benefit || ''}
                onChange={(e) => {
                  const benefit = e.target.value || null;
                  const updates = { vet_staff_benefit: benefit };
                  // Auto-apply 10% discount to total_cost when selected
                  if (benefit === 'discount_10' && formData.total_cost) {
                    const original = parseFloat(formData.total_cost);
                    if (!isNaN(original) && original > 0) {
                      updates.total_cost = (original * 0.9).toFixed(2);
                    }
                  }
                  setFormData({ ...formData, ...updates });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              >
                <option value="">None</option>
                <option value="discount_10">10% Vet Staff Discount</option>
                <option value="complimentary_urn">Complimentary Urn</option>
              </select>
              {formData.vet_staff_benefit === 'discount_10' && (
                <p className="text-xs text-green-600 mt-1 font-medium">🏷️ 10% discount applied to total — also reflected on QuickBooks invoice</p>
              )}
              {formData.vet_staff_benefit === 'complimentary_urn' && (
                <p className="text-xs text-purple-600 mt-1 font-medium">🏺 Urn will show as $0 on QuickBooks invoice — client selects urn at facility</p>
              )}
              {!formData.vet_staff_benefit && (
                <p className="text-xs text-gray-400 mt-1">Applied automatically to QuickBooks invoice when set</p>
              )}
            </div>
          </div>

          {/* Mobile Save Button */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t p-4">
            <div className="flex space-x-4 max-w-6xl mx-auto">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed text-lg"
              >
                {loading ? 'Saving...' : (isEdit ? 'Update Pet Record' : 'Add Pet Record')}
              </button>
            </div>
          </div>

          {/* Desktop Save Buttons */}
          <div className="hidden lg:flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : (isEdit ? 'Update Pet Record' : 'Add Pet Record')}
            </button>
          </div>

          {/* Mobile padding for floating button */}
          <div className="lg:hidden h-20"></div>
        </form>

        {/* Keepsake Confirmation Dialog - FOR LOCATION TRANSFERS */}
        {showKeepsakeConfirmation && keepsakeData && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60">
            <div className="bg-white rounded-xl max-w-md w-full mx-4 p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                  <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  ⚠️ Keepsakes Must Be Collected First
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  This pet has selected keepsakes that must be collected before moving to cremation.
                </p>
                <div className="bg-yellow-50 p-3 rounded-lg mb-4">
                  <p className="text-xs font-medium text-yellow-800 mb-2">Pet's Selected Keepsakes:</p>
                  <ul className="text-xs text-yellow-700">
                    {(keepsakeData.keepsakes || []).map((keepsake, index) => (
                      <li key={index}>• {keepsake}</li>
                    ))}
                  </ul>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleKeepsakeCancelled}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel Transfer
                  </button>
                  <button
                    onClick={handleKeepsakeConfirmed}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    ✅ Confirm: Keepsakes Collected
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default AddPetModal;
